import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { UserClaims } from '@okta/okta-auth-js';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { AccountProfile } from '@tcs/client-portal-api/src/types/AccountProfile';
import { useQuery } from '@tcs/common/hooks/useQuery';
import { Persona } from '@tcs/client-portal-api/types/Persona';
import { useAnalytics } from '@tcs/common/hooks/analytics';
import { Representative } from '@tcs/client-portal-api/types/Representative';
import { getAgeOnDate } from '@tcs/client-portal-api/utils/getAgeOnDate';
import { Account } from '@tcs/client-portal-api/types/Account';
import { useProfile } from '@tcs/client-portal-common/hooks/useProfile';

type ClientPortalClaims = UserClaims & {
  clientPortalPersona?: Persona;
  [clientPortalIdentity: string]: any;
};

interface ContextProps {
  loading: boolean;
  claims?: ClientPortalClaims;
  profile?: AccountProfile;
}

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionContext = createContext<ContextProps>({
  loading: true,
  claims: undefined,
  profile: undefined,
});

const SessionProvider = ({ children }: SessionProviderProps) => {
  const { authState, oktaAuth } = useOktaAuth();
  const [userClaims, setUserClaims] = useState<ClientPortalClaims>();
  const analytics = useAnalytics();
  const launchDarklyClient = useLDClient();

  useEffect(() => {
    (async () => {
      if (authState?.isAuthenticated) {
        const userClaimsResult = await oktaAuth.getUser();
        setUserClaims(userClaimsResult);

        launchDarklyClient.identify({
          key: userClaimsResult['clientPortalIdentity'].toString(),
        });
      }
    })();
  }, [analytics, authState, launchDarklyClient, oktaAuth]);

  const fetchIndividualData =
    authState.isAuthenticated &&
    userClaims !== undefined &&
    userClaims?.clientPortalPersona === Persona.Individual;

  const { data: profile, isLoading: profileLoading } = useProfile(
    userClaims?.clientPortalIdentity,
    { enabled: fetchIndividualData }
  );

  const { data: account, isLoading: accountLoading } = useQuery<Account, Error>(
    `/accounts/${userClaims?.clientPortalIdentity}`,
    {
      enabled: fetchIndividualData,
    }
  );

  const { data: representative } = useQuery<Representative, Error>(
    `/representatives/${userClaims?.clientPortalIdentity}`,
    {
      enabled:
        authState.isAuthenticated &&
        userClaims !== undefined &&
        userClaims?.clientPortalPersona === Persona.Representative,
    }
  );

  useEffect(() => {
    if (!analytics || !userClaims) {
      return;
    }

    if (
      userClaims.clientPortalPersona === Persona.Individual &&
      (profileLoading || accountLoading)
    ) {
      return;
    }

    if (
      userClaims.clientPortalPersona === Persona.Representative &&
      !representative
    ) {
      return;
    }

    const userProps: Record<string, string | number> = {
      persona: userClaims.clientPortalPersona,
    };

    switch (userClaims.clientPortalPersona) {
      case Persona.Individual:
        userProps.email = profile.contactInfo.email;
        userProps.age = getAgeOnDate(
          profile.personalInfo.dateOfBirth,
          new Date()
        );
        if (account) {
          userProps['Account Total Value'] = account.totalValue;
          userProps['Account Is RMD'] = `${account.isInRMDStatus}`;
        }
        break;
      case Persona.Representative:
        userProps.email = representative.contactInfo.email;
        break;
    }
    analytics.identifyUser(userClaims.clientPortalIdentity, userProps);
  }, [analytics, userClaims, accountLoading, profileLoading, representative]);

  return (
    <SessionContext.Provider
      value={{
        loading: profileLoading,
        claims: userClaims,
        profile: profile ?? {
          personalInfo: null,
          address: representative?.address,
          contactInfo: representative?.contactInfo,
        },
      }}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;
